import { useState } from 'preact/hooks';
import { startRegistration, browserSupportsWebAuthn } from '@simplewebauthn/browser';
import { Knot } from '../components/Knot';
import { setStoredTenantId } from '../lib/tenant';
import {
  createTenant,
  updateActorProfile,
  passkeyRegisterOptions,
  passkeyRegisterVerify,
  ApiError,
  type ApiUser,
} from '../lib/api';
import './Onboarding.css';

export interface OnboardingProps {
  /** The just-signed-in user (a brand-new account with zero workspaces). */
  user: ApiUser;
  /** The calm, non-blocking "we emailed you a link to confirm your address"
   *  notice carried over from a fresh password registration. */
  notice?: string;
  /** Handed the newly-created tenantId once setup completes; App stores it as
   *  active and drops into first-run Today. */
  onResolved: (tenantId: string) => void;
}

// The actor's setup vertical — set FOR the user (the word "vertical" is never
// shown; per Huan 2026-07-13 first-run collects NAME + UNION only).
const VERTICAL = 'actors';

const UNIONS = ['ACTRA', 'SAG-AFTRA', 'Both', 'Non-union'] as const;
type Union = (typeof UNIONS)[number];

// Map a chip to what actually gets stored as union_name. "Both" is the two
// unions; "Non-union" is recorded verbatim (free-text field) rather than left
// blank, so a later screen can tell "chose non-union" from "skipped".
const UNION_NAME: Record<Union, string> = {
  ACTRA: 'ACTRA',
  'SAG-AFTRA': 'SAG-AFTRA',
  Both: 'ACTRA / SAG-AFTRA',
  'Non-union': 'Non-union',
};

type Step = 'name' | 'union' | 'creating' | 'passkey';

function defaultName(user: ApiUser): string {
  const trimmed = user.name?.trim();
  if (trimmed) return trimmed;
  const [local] = user.email.split('@');
  return local ?? '';
}

// Auth-2 first-run (new actor, zero tenants) — matches the blessed
// onboarding_flow.html column B: name, then union chips, progress dots, the
// breathing Knot on the "all set" beat, then first-run Today. Everything else
// (home base, reps, materials) is deferred. An OPTIONAL Face/Touch ID prompt
// follows setup where the browser supports it.
export function Onboarding({ user, notice, onResolved }: OnboardingProps) {
  const [step, setStep] = useState<Step>('name');
  const [name, setName] = useState(defaultName(user));
  const [union, setUnion] = useState<Union | null>('ACTRA');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Set once the tenant + profile are written, so a passkey-step failure/skip
  // never re-creates the workspace.
  const [tenantId, setTenantId] = useState<string | null>(null);

  // Creates the workspace + writes name/union. `withUnion` false = the union
  // step was skipped, so no union_name is stored.
  async function createWorkspace(withUnion: boolean) {
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Your name is needed to set up.');
      setStep('name');
      return;
    }
    setStep('creating');
    setError(null);
    setBusy(true);
    try {
      const { tenantId: newTenantId } = await createTenant(trimmed, VERTICAL);
      setStoredTenantId(newTenantId);
      setTenantId(newTenantId);
      const fields: Record<string, unknown> = { professional_name: trimmed };
      if (withUnion && union) fields.union_name = UNION_NAME[union];
      await updateActorProfile(newTenantId, fields);
      // Offer Face/Touch ID only where the browser can actually do it.
      if (browserSupportsWebAuthn()) {
        setStep('passkey');
      } else {
        onResolved(newTenantId);
      }
    } catch (err) {
      setBusy(false);
      setError(
        err instanceof ApiError ? "We couldn't finish setup. Please try again." : 'Something went wrong.'
      );
      setStep('union');
    }
  }

  async function registerPasskey() {
    if (!tenantId) return;
    setBusy(true);
    setError(null);
    try {
      const { ceremonyId, options } = await passkeyRegisterOptions();
      const response = await startRegistration({
        optionsJSON: options as Parameters<typeof startRegistration>[0]['optionsJSON'],
      });
      await passkeyRegisterVerify(ceremonyId, response);
      onResolved(tenantId);
    } catch {
      // A declined/failed passkey must never block finishing — just proceed.
      onResolved(tenantId);
    }
  }

  if (step === 'creating') {
    return (
      <div class="onb">
        <div class="onb__center">
          <Knot size={46} />
          <div class="onb__say onb__say--tight">Setting up your workspace…</div>
        </div>
      </div>
    );
  }

  if (step === 'passkey') {
    return (
      <div class="onb">
        <div class="onb__center">
          <Knot size={46} />
          <div class="onb__eyebrow">One more thing</div>
          <div class="onb__say">
            Faster sign-in?
            <br />
            <span class="onb__say-soft">
              Use Face or Touch ID next time instead of a password.
            </span>
          </div>
        </div>
        <button type="button" class="onb__btn" disabled={busy} onClick={() => void registerPasskey()}>
          {busy ? 'Setting up…' : 'Set up Face / Touch ID'}
        </button>
        <button
          type="button"
          class="onb__textlink"
          disabled={busy}
          onClick={() => onResolved(tenantId!)}
        >
          Skip for now
        </button>
      </div>
    );
  }

  if (step === 'union') {
    return (
      <div class="onb">
        <div class="onb__dots">
          <i class="onb__dot onb__dot--on" />
          <i class="onb__dot onb__dot--on" />
        </div>
        <div class="onb__eyebrow">A little about you</div>
        <div class="onb__say">
          Your union.
          <br />
          <span class="onb__say-soft">You can change this later.</span>
        </div>
        <div class="onb__chips" role="group" aria-label="Union">
          {UNIONS.map((u) => (
            <button
              type="button"
              key={u}
              class={`onb__chip${union === u ? ' onb__chip--on' : ''}`}
              aria-pressed={union === u}
              onClick={() => setUnion(u)}
            >
              {u}
            </button>
          ))}
        </div>
        {error ? (
          <div class="onb__error" role="alert">
            {error}
          </div>
        ) : null}
        <div class="onb__grow" />
        <button type="button" class="onb__btn" disabled={busy} onClick={() => void createWorkspace(true)}>
          Continue
        </button>
        <button
          type="button"
          class="onb__textlink"
          disabled={busy}
          onClick={() => void createWorkspace(false)}
        >
          Skip for now
        </button>
      </div>
    );
  }

  // step === 'name'
  return (
    <div class="onb">
      <div class="onb__dots">
        <i class="onb__dot onb__dot--on" />
        <i class="onb__dot" />
      </div>
      {notice ? <div class="onb__notice">{notice}</div> : null}
      <div class="onb__eyebrow">Welcome — let’s set up</div>
      <div class="onb__say">
        First, your name.
        <br />
        <span class="onb__say-soft">The one that goes on your resume and materials.</span>
      </div>
      <label class="onb__label" for="onb-name">
        Professional name
      </label>
      <input
        id="onb-name"
        class="onb__input"
        type="text"
        autocomplete="name"
        placeholder="e.g. Alex Rivera"
        value={name}
        onInput={(e) => setName((e.target as HTMLInputElement).value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && name.trim()) setStep('union');
        }}
      />
      {error ? (
        <div class="onb__error" role="alert">
          {error}
        </div>
      ) : null}
      <div class="onb__grow" />
      <button
        type="button"
        class="onb__btn"
        disabled={!name.trim()}
        onClick={() => {
          setError(null);
          setStep('union');
        }}
      >
        Continue
      </button>
    </div>
  );
}
